import React from 'react';
import './analysistool.css';
import './common';
import './delegate';
import './drawbar';
import './drawingengine';
import './drawtools';
import './dual';
import './dualvideocontrol';
import './events'; // Events must be before other imports.
import './icon';
import { AnalysisTool } from './shim';
import './single';
import './singlevideocontrol';
import './syncbar';
import './timeline';
import {
  CaptureCallback,
  ICaptureCompleteEventParams,
  ISingleAnalysisCaptureState,
  ISingleAnalysisTool,
  ISingleAnalysisToolState
} from './types';
import './videoplayer';

/** Configuration properties for an AnalysisToolComponent. */
export interface IAnalysisToolComponentProps {
  /** Fully qualified URL to media item to play. */
  mediaUrl: string;
  /** Fully qualified URL to poster image, if the media is a video. */
  thumbUrl?: string;
  /** Optional width of the analysis tool. */
  width?: number;
  /** Optional height of the analysis tool. */
  height?: number;
  /** Optional value indicating that the timeline range handles should be hidden. */
  showRangeHandles?: boolean;
}

interface IAnalysisToolComponentState {
  // targetID: string;
  // currentTool: ISingleAnalysisTool | void;
}

/** Returns a unique identifier each time it's called. */
const uniqueTargetID = (() => {
  let id = 1;
  return () => `__analysis-${id++}__`;
})();

/**
 * Analysis tool component to run the Bracken 1 JavaScript analysis tool.
 */

class AnalysisToolComponent extends React.Component<
  IAnalysisToolComponentProps,
  IAnalysisToolComponentState
> {
  /** Reference to container housing the analysis tool. */
  private _containerRef: React.RefObject<HTMLDivElement>;
  /** Unique DOM identifier for analysis tool. */
  private _targetID: string = uniqueTargetID();
  /** The analysis tool controlled by this component. */
  private _tool: ISingleAnalysisTool | undefined;

  /**
   * Trigger a capture. The returned promise is resolved with the captured image
   * and state, or rejected if the tool is not available.
   * @param region Optional value indicating that a partial region should be
   * captured. (The analysis tool has built-in interface to select the region.)
   */
  public triggerCapture: (
    region?: boolean
  ) => Promise<ICaptureCompleteEventParams | undefined> =
    this._triggerCapture.bind(this);

  /**
   * Show the given image in the analysis tool window.
   * @param image Source or data URL of image to show.
   */
  public showImage: (image: string) => void = this._showImage.bind(this);

  /**
   * Hide any image currently in the analysis tool window.
   */
  public hideImage = this._hideImage.bind(this);

  /**
   * Update the state of the tool to one obtained with getState, e.g. when an
   * image was captured.
   * @param state State to set the tool to.
   */
  public setState: (state: ISingleAnalysisToolState) => void =
    this._setState.bind(this);

  /**
   * Initialize a new instance of the AnalysisToolComponent class.
   * @param props Properties to set for the tool.
   */
  constructor(props: IAnalysisToolComponentProps) {
    super(props);
    this._containerRef = React.createRef<HTMLDivElement>();
    this.state = {
      targetID: uniqueTargetID(),
      currentTool: undefined
    };
  }

  /**
   * Finish setup after the component has mounted.
   */
  public componentDidMount(): void {
    this._tool = new (AnalysisTool.singleAnalysisTool as any)({
      mediaUrl: [this.props.mediaUrl || '', this.props.thumbUrl || ''],
      targetID: this._targetID,
      height: this.props.height || 500
    }) as ISingleAnalysisTool;
    this._tool.render();
    this.updateRangeHandles();
  }

  /**
   * Component properties or state has changed.
   */
  public componentDidUpdate(
    prevProps: Readonly<IAnalysisToolComponentProps>,
    prevState: Readonly<IAnalysisToolComponentState>,
    snapshot?: any
  ): void {
    const tool = this._tool;
    if (!tool) {
      return;
    }
    const props = this.props;
    if (
      props.mediaUrl !== prevProps.mediaUrl ||
      props.thumbUrl !== prevProps.thumbUrl
    ) {
      this.updateMedia();
    }
    if (props.width !== prevProps.width || props.height !== prevProps.height) {
      this.updateSize();
    }
    if (props.showRangeHandles !== prevProps.showRangeHandles) {
      this.updateRangeHandles();
    }
  }

  /**
   * The component is about to be unmounted; destroy analysis tool and
   * unsubscribe from services.
   */
  public componentWillUnmount(): void {
    if (this._tool) {
      this._tool.setCaptureCallback(undefined, false);
    }
    if (this._containerRef?.current) {
      this._containerRef.current.innerHTML = '';
    }
  }

  /**
   * React-invoked render method.
   */
  render() {
    return (
      <div
        ref={this._containerRef}
        id={this._targetID}
        className="ac-container"
      ></div>
    );
  }

  // ------------------------------------------

  /**
   * Update the tool dimensions based on the current props.
   */
  private updateSize() {
    this._tool.resizeTool(this.props.width, this.props.height);
  }

  /**
   * Update the tool media based on the current props.
   */
  private updateMedia() {
    this._tool.loadMedia([
      this.props.mediaUrl || '',
      this.props.thumbUrl || ''
    ]);
  }

  /**
   * Update the range handles on the tool container.
   */
  private updateRangeHandles() {
    if (this._containerRef?.current) {
      this._containerRef.current.setAttribute(
        'data-range-handles',
        this.props.showRangeHandles ? 'true' : 'false'
      );
    }
  }

  /**
   * Trigger a capture. The returned promise is resolved with the captured image
   * and state, or rejected if the tool is not available.
   * @param region Optional value indicating that a partial region should be
   * captured. (The analysis tool has built-in interface to select the region.)
   */
  private _triggerCapture(
    region?: boolean
  ): Promise<ICaptureCompleteEventParams | void> {
    const _this = this;
    return new Promise((resolve, reject) => {
      if (!_this._tool) {
        reject('Analysis tool not initialized');
      }
      /** Callback for the tool after completing the capture. */
      const captureCallback: CaptureCallback = (_, data) => {
        if (data) {
          const base64 = data.ImageNameGuid as string;
          const state: ISingleAnalysisCaptureState = JSON.parse(
            _this._tool.getState() || '{}'
          );
          resolve({ base64, state });
        } else {
          resolve();
        }
      };

      _this._tool.setCaptureCallback(captureCallback, false);
      _this._tool.captureImage(region);
    });
  }

  /**
   * Show an image in the analysis window.
   * @param image Source or data URL of image to show.
   */
  private _showImage(image: string) {
    this._tool?.showImage(image);
  }

  /**
   * Hide any image currently in the analysis window.
   */
  private _hideImage() {
    this._tool?.hideImage();
  }

  /**
   * Update the state of the tool to one obtained with getState, e.g. when an
   * image was captured.
   * @param state State to set the tool to.
   */
  private _setState(state: ISingleAnalysisToolState) {
    this._tool?.setState(state);
  }
}

export default AnalysisToolComponent;
